import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from "@mui/material";
import { CheckCircle, Payment } from "@mui/icons-material";
import { formatCurrency } from "@nomnom/core";
import type { Payout, RiderProfile, User } from "../types/firestore";
import { useAdminAuth } from "../context/AdminAuthContext";
import {
  formatPeriod,
  formatTimestamp,
  loadPayouts as loadPayoutData,
  markPayoutFailed,
  markPayoutPaid,
} from "../services/payoutsService";

export default function Payouts() {
  const { user } = useAdminAuth();
  const [payouts, setPayouts] = useState<(Payout & { id: string })[]>([]);
  const [riderProfiles, setRiderProfiles] = useState<
    Record<string, RiderProfile>
  >({});
  const [users, setUsers] = useState<Record<string, User>>({});
  const [loading, setLoading] = useState(true);
  const [markingPaid, setMarkingPaid] = useState<string | null>(null);
  const [markPaidDialog, setMarkPaidDialog] = useState<{
    payout: Payout & { id: string };
    rider: RiderProfile | null;
  } | null>(null);
  const [paidMethod, setPaidMethod] = useState("");
  const [paidNotes, setPaidNotes] = useState("");
  const [failingPayoutId, setFailingPayoutId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadPayoutDataForPage = async () => {
    try {
      setLoading(true);
      setError(null);
      const snapshot = await loadPayoutData();
      setPayouts(snapshot.payouts);
      setRiderProfiles(snapshot.riderProfiles);
      setUsers(snapshot.users);
    } catch (err) {
      console.error("[Payouts] Failed to load:", err);
      setError(err instanceof Error ? err.message : "Failed to load payouts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadPayoutDataForPage();
  }, []);

  const handleOpenMarkPaid = (payout: Payout & { id: string }) => {
    const rider = riderProfiles[payout.riderUid] ?? null;
    setMarkPaidDialog({ payout, rider });
    setPaidMethod(rider?.payoutMethod ?? "venmo");
    setPaidNotes("");
  };

  const handleMarkPaid = async () => {
    if (!markPaidDialog) return;
    const { payout } = markPaidDialog;

    setMarkingPaid(payout.id);
    setError(null);
    try {
      await markPayoutPaid(
        payout.id,
        paidMethod,
        payout.paymentMethod,
        paidNotes,
        user?.uid ?? null,
      );
      setMarkPaidDialog(null);
      await loadPayoutDataForPage();
    } catch (err) {
      console.error("[Payouts] Failed to mark paid:", err);
      setError(err instanceof Error ? err.message : "Failed to update payout");
    } finally {
      setMarkingPaid(null);
    }
  };

  const handleMarkFailed = async (payout: Payout & { id: string }) => {
    setFailingPayoutId(payout.id);
    setError(null);
    try {
      await markPayoutFailed(payout.id, user?.uid ?? null);
      await loadPayoutDataForPage();
    } catch (err) {
      console.error("[Payouts] Failed to mark payout failed:", err);
      setError(err instanceof Error ? err.message : "Failed to update payout");
    } finally {
      setFailingPayoutId(null);
    }
  };

  const pendingCount = payouts.filter((p) => p.status === "pending").length;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Rider Payouts
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Payouts are created by the <code>calculateWeeklyPayouts</code> Cloud
        Function. Use this page to review weekly payout batches and mark them
        paid after sending manual Mercury ACH transfers to riders.
      </Typography>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : payouts.length === 0 ? (
        <Paper sx={{ p: 4 }}>
          <Typography color="text.secondary">
            No payouts yet. Run the weekly payout calculation from your backend
            or Firebase Console to create payout records.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Payouts ({pendingCount} pending)
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell>Rider</TableCell>
                  <TableCell>Payout Info</TableCell>
                  <TableCell>Breakdown</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payouts.map((payout) => {
                  const rider = riderProfiles[payout.riderUid];
                  const payoutUser = users[payout.riderUid];
                  const displayName =
                    rider?.legalName ??
                    payoutUser?.displayName ??
                    rider?.ucsdEmail ??
                    payout.riderUid.slice(0, 8);
                  const payoutInfo =
                    payout.paymentMethod === "mercury_ach" ||
                    rider?.payoutMethod === "mercury_ach"
                      ? "Manual Mercury ACH"
                      : payout.venmoHandleSnapshot
                        ? `Venmo: @${payout.venmoHandleSnapshot}`
                        : rider?.venmoHandle
                          ? `Venmo: @${rider.venmoHandle}`
                          : "Payout rail not configured";
                  const payoutStateLabel = rider?.payoutEligible
                    ? "Eligible"
                    : rider?.payoutHoldReason
                      ? `On hold: ${rider.payoutHoldReason}`
                      : "On hold";

                  return (
                    <TableRow key={payout.id}>
                      <TableCell>{formatPeriod(payout.period)}</TableCell>
                      <TableCell>{displayName}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {payoutInfo}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {payoutStateLabel}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          Fee ${Number(payout.feeAmount ?? 0).toFixed(2)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Tip ${Number(payout.tipAmount ?? 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(payout.grossAmount ?? payout.amount)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={payout.status}
                          color={
                            payout.status === "paid"
                              ? "success"
                              : payout.status === "pending"
                                ? "warning"
                                : payout.status === "failed"
                                  ? "error"
                                  : "default"
                          }
                          icon={
                            payout.status === "paid" ? (
                              <CheckCircle fontSize="small" />
                            ) : undefined
                          }
                        />
                      </TableCell>
                      <TableCell>{formatTimestamp(payout.createdAt)}</TableCell>
                      <TableCell align="right">
                        {payout.status === "pending" && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "flex-end",
                              flexWrap: "wrap",
                            }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Payment />}
                              onClick={() => handleOpenMarkPaid(payout)}
                              disabled={!!markingPaid || !!failingPayoutId}
                            >
                              Mark Paid
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="text"
                              onClick={() => void handleMarkFailed(payout)}
                              disabled={!!markingPaid || !!failingPayoutId}
                            >
                              Mark Failed
                            </Button>
                          </Box>
                        )}
                        {payout.status === "paid" && payout.paidAt && (
                          <Typography variant="body2" color="text.secondary">
                            Paid {formatTimestamp(payout.paidAt)}
                          </Typography>
                        )}
                        {payout.status === "failed" && payout.failedAt && (
                          <Typography variant="body2" color="error">
                            Failed {formatTimestamp(payout.failedAt)}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog
        open={!!markPaidDialog}
        onClose={() => !markingPaid && setMarkPaidDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mark Payout as Paid</DialogTitle>
        <DialogContent>
          {markPaidDialog && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
            >
              <Typography>
                Amount:{" "}
                <strong>
                  {formatCurrency(
                    markPaidDialog.payout.grossAmount ??
                      markPaidDialog.payout.amount,
                  )}
                </strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rider:{" "}
                {markPaidDialog.rider?.legalName ??
                  users[markPaidDialog.payout.riderUid]?.displayName ??
                  markPaidDialog.payout.riderUid}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Payout rail:{" "}
                {markPaidDialog.payout.paymentMethod === "mercury_ach" ||
                markPaidDialog.rider?.payoutMethod === "mercury_ach"
                  ? "Manual Mercury ACH"
                  : `Venmo: @${
                      markPaidDialog.payout.venmoHandleSnapshot ??
                      markPaidDialog.rider?.venmoHandle ??
                      "not-set"
                    }`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Breakdown: Fee $
                {Number(markPaidDialog.payout.feeAmount ?? 0).toFixed(2)} / Tip
                ${Number(markPaidDialog.payout.tipAmount ?? 0).toFixed(2)}
              </Typography>
              <TextField
                label="Payment method used"
                value={paidMethod}
                onChange={(e) => setPaidMethod(e.target.value)}
                placeholder="manual_mercury_ach"
                fullWidth
              />
              <TextField
                label="Notes (optional)"
                value={paidNotes}
                onChange={(e) => setPaidNotes(e.target.value)}
                placeholder="e.g., Mercury payment reference"
                fullWidth
                multiline
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMarkPaidDialog(null)}
            disabled={!!markingPaid}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMarkPaid}
            disabled={!!markingPaid}
          >
            {markingPaid ? "Updating..." : "Mark as Paid"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
